/**
 * What has not become a golden record, and why.
 *
 * A quarantined record is not a silent drop: it names the rule it failed, so
 * the fix is either the data or the rule, and both are one click away.
 */

import { useState } from 'react'
import { EmptyState, Modal, SkeletonRows } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { useEditStagedRecord, useResubmitRecord, useStagedRecords } from '../hooks'
import type { GraphModel, StagedRecordRow } from '../types/brain.types'

const STATUS_CLASS: Record<string, string> = {
  PENDING: 'badge-warn',
  RESOLVED: 'badge-ok',
  QUARANTINED: 'badge-danger',
}

export function StagedRecords({
  graphId,
  model,
  nodeTypeKey,
}: {
  graphId: string
  model: GraphModel
  /** Empty means every entity — the picker above this view owns the choice. */
  nodeTypeKey?: string
}) {
  const [status, setStatus] = useState('QUARANTINED')
  const [editing, setEditing] = useState<StagedRecordRow | null>(null)
  const staged = useStagedRecords(graphId, {
    status: status || undefined,
    node_type_key: nodeTypeKey || undefined,
  })
  const resubmit = useResubmitRecord(graphId)
  const toast = useToast()

  async function sendBack(record: StagedRecordRow) {
    try {
      await resubmit.mutateAsync(record.id)
      toast.success(
        'Record resubmitted',
        'It re-enters the pipeline at stage one — quality rules first, then matching.',
      )
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <div className="stack">
      <div className="row">
        <select
          className="select"
          style={{ maxWidth: 220 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="QUARANTINED">Quarantined</option>
          <option value="PENDING">Waiting to be resolved</option>
          <option value="RESOLVED">Resolved</option>
          <option value="">All</option>
        </select>
        {staged.data && (
          <span className="small muted">{staged.data.meta.total.toLocaleString()} records</span>
        )}
      </div>

      {staged.isLoading && <SkeletonRows rows={6} columns={5} label="Loading records…" />}

      {staged.data?.records.length === 0 && (
        <EmptyState title={status === 'QUARANTINED' ? 'Nothing quarantined' : 'Nothing here'}>
          {status === 'QUARANTINED'
            ? 'Every record that has come through so far passed its quality rules.'
            : 'Ingest some rows and run the pipeline to see records here.'}
        </EmptyState>
      )}

      {staged.data && staged.data.records.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Node type</th>
                <th>Extracted values</th>
                <th>Confidence</th>
                <th>Status</th>
                <th>Why it stopped</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {staged.data.records.map((record) => (
                <tr key={record.id}>
                  <td>
                    <strong>{record.node_type_key}</strong>
                    <div className="small faint mono">{record.temp_id}</div>
                  </td>
                  <td className="mono small">
                    {Object.entries(record.properties)
                      .slice(0, 4)
                      .map(([k, v]) => `${k}: ${String(v)}`)
                      .join(' · ') || '—'}
                  </td>
                  <td className="mono small">{record.confidence.toFixed(2)}</td>
                  <td>
                    <span className={`badge ${STATUS_CLASS[record.resolve_status]}`}>
                      {record.resolve_status}
                    </span>
                  </td>
                  <td className="small muted">{record.quarantine_reason ?? '—'}</td>
                  <td>
                    {/* Two steps, not one. Fixing three fields on a record
                        should not put it through the pipeline three times. */}
                    <div className="row-tight" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(record)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm"
                        disabled={resubmit.isPending}
                        onClick={() => sendBack(record)}
                        title="Runs this record through the rules again, from the beginning."
                      >
                        Resubmit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status === 'QUARANTINED' && (staged.data?.meta.total ?? 0) > 0 && (
        <p className="small faint" style={{ margin: 0 }}>
          Each of these failed an ERROR-severity quality rule. Either the source data needs
          fixing, or the rule is stricter than it should be — both live on the node type's Data
          quality page.
          {model.node_types.length > 0 && ' '}
        </p>
      )}
      {editing && (
        <EditStagedDialog
          graphId={graphId}
          record={editing}
          nodeType={model.node_types.find((n) => n.key === editing.node_type_key)}
          onClose={() => setEditing(null)}
          onResubmit={sendBack}
        />
      )}
    </div>
  )
}

/**
 * Correct the values on a record that never became golden.
 *
 * The violated rule is named at the top, because the edit only makes sense
 * against it — and saving deliberately leaves the record where it is. Nothing
 * re-enters the pipeline until someone says so.
 *
 * The whole record is shown, not only the values the mapping kept: every
 * property the node type has (so a missing one can be filled in), and beside
 * them the raw row as the source sent it — which is usually where the missing
 * value is sitting under a name the mapping did not know.
 */
function EditStagedDialog({
  graphId,
  record,
  nodeType,
  onClose,
  onResubmit,
}: {
  graphId: string
  record: StagedRecordRow
  nodeType?: { properties: { key: string; display_name?: string | null; is_required?: boolean }[] }
  onClose: () => void
  onResubmit: (record: StagedRecordRow) => void
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {}
    // The model's properties first, in the model's order, blank where the
    // record has nothing; then anything the record carries that the model
    // does not name.
    for (const p of nodeType?.properties ?? []) seed[p.key] = ''
    for (const [k, v] of Object.entries(record.properties ?? {})) seed[k] = v == null ? '' : String(v)
    return seed
  })
  const [showSource, setShowSource] = useState(true)
  const required = new Set((nodeType?.properties ?? []).filter((p) => p.is_required).map((p) => p.key))
  const source = record.source_payload ?? null
  const edit = useEditStagedRecord(graphId)
  const toast = useToast()

  async function save(thenResubmit: boolean) {
    try {
      await edit.mutateAsync({ recordId: record.id, properties: values })
      if (thenResubmit) onResubmit(record)
      else toast.success('Values saved', 'The record stays here until you resubmit it.')
      onClose()
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      wide
      title={`Edit this ${record.node_type_key}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn" onClick={() => save(false)} disabled={edit.isPending}>
            Save
          </button>
          <button className="btn btn-primary" onClick={() => save(true)} disabled={edit.isPending}>
            Save and resubmit
          </button>
        </>
      }
    >
      <div className="stack">
        {record.quarantine_reason && (
          <div className="callout callout-warn">
            <p className="small" style={{ margin: 0 }}>
              {record.quarantine_reason}
            </p>
          </div>
        )}
        {Object.keys(values).length === 0 && (
          <p className="muted small">This record has no values to correct.</p>
        )}
        <div className="rec-editgrid">
          <div className="stack" style={{ gap: 8 }}>
            <span className="small faint">Record · {Object.keys(values).length} properties</span>
            {Object.entries(values).map(([key, value]) => (
              <label key={key} className="stack" style={{ gap: 4 }}>
                <span className="small mono">
                  {key}
                  {required.has(key) && (
                    <span className="field-req" title="Required by the model">
                      {' '}
                      *
                    </span>
                  )}
                  {value === '' && source && key in source && (
                    <button
                      type="button"
                      className="rec-link small"
                      style={{ marginLeft: 8 }}
                      onClick={() =>
                        setValues((prev) => ({ ...prev, [key]: String(source[key] ?? '') }))
                      }
                    >
                      use source value
                    </button>
                  )}
                </span>
                <input
                  className="input"
                  value={value}
                  placeholder={value === '' ? 'empty' : undefined}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          {source && (
            <div className="stack" style={{ gap: 8 }}>
              <button
                type="button"
                className="small faint"
                style={{ border: 0, background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                onClick={() => setShowSource((v) => !v)}
              >
                Source row · {Object.keys(source).length} columns {showSource ? '▾' : '▸'}
              </button>
              {showSource && (
                <div className="rec-sourcerow">
                  {Object.entries(source).map(([k, v]) => (
                    <div className="cp-erow" key={k}>
                      <span className="lab mono">{k}</span>
                      <span className="to small mono" title={typeof v === 'string' ? v : JSON.stringify(v)}>
                        {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
